import { Wall } from "src/types/configuration";
import ConfiguratorService from "src/services/configuratorService";

const OPENING_SIDE_PADDING = 400; // 200mm on each side
const OPENING_BETWEEN_GAP = 400; // 400mm between door and window

/**
 * Wall Validation System
 *
 * This module provides automatic validation and cleanup of doors and windows
 * that no longer fit in wall sections due to dimension or configuration changes.
 *
 * HOW IT WORKS:
 *
 * 1. Automatic Cleanup (useSynchronizeServices):
 *    - When dimensions change (width, depth, storage widths)
 *    - When roof type changes (e.g., kapschuur has special restrictions)
 *    - When storages are added/removed
 *    - validateAndCleanWalls() automatically removes invalid doors/windows
 *
 * 2. UI Prevention (WindowSelect/DoorSelect):
 *    - getDisabledWindows() / getDisabledDoors() compute which options to disable
 *    - Users cannot select doors/windows that don't fit
 *    - Handles both standalone and combined door+window scenarios
 *
 * 3. Validation Rules:
 *    - Each section needs 200mm padding on each side (total 400mm)
 *    - If both door and window exist, need additional 400mm between them
 *    - When space is insufficient: remove window first, then door if needed
 *    - Kapschuur roof type: no doors/windows on h-1 walls
 *    - If section width = 0, all doors/windows are removed
 *
 * USAGE:
 * - This system works globally - no component-specific setup needed
 * - WindowSelect and DoorSelect components automatically use helper functions
 * - useSynchronizeServices automatically validates on state changes
 */

/**
 * Validates and cleans up invalid doors and windows from walls
 * based on section width constraints
 */
export function validateAndCleanWalls(
  walls: Wall[],
  configurator: ConfiguratorService,
  roofType: string,
  shouldValidate?: (wall: Wall) => boolean
): Wall[] {
  const shouldValidateWall = shouldValidate ?? (() => true);

  return walls.map((wall) => {
    if (!shouldValidateWall(wall)) {
      return wall;
    }

    // Only process walls that have wood walls
    if (wall.woodWall.outerColor === "empty") {
      return wall;
    }

    const isKapschuurSide = roofType === "kapschuur" && wall.id.startsWith("v");
    const kapschuurSideOffset = isKapschuurSide ? 100 : 0;

    const updatedWall = { ...wall };
    const sectionWidth = configurator.getSectionWidth(wall);
    const padding = OPENING_SIDE_PADDING + kapschuurSideOffset;

    // Special case: kapschuur roof type on h-1 walls - remove all doors and windows
    const isKapschuurH1 = roofType === "kapschuur" && wall.id.startsWith("h-1");

    if (isKapschuurH1) {
      if (updatedWall.woodWall.door !== "empty") {
        updatedWall.woodWall = {
          ...updatedWall.woodWall,
          door: "empty",
        };
      }
      if (updatedWall.woodWall.window !== "empty") {
        updatedWall.woodWall = {
          ...updatedWall.woodWall,
          window: "empty",
        };
      }
      return updatedWall;
    }

    // If section width cannot be determined, remove door and window
    if (sectionWidth === 0) {
      if (updatedWall.woodWall.door !== "empty") {
        updatedWall.woodWall = {
          ...updatedWall.woodWall,
          door: "empty",
        };
      }
      if (updatedWall.woodWall.window !== "empty") {
        updatedWall.woodWall = {
          ...updatedWall.woodWall,
          window: "empty",
        };
      }
      return updatedWall;
    }

    const currentDoor = updatedWall.woodWall.door;
    const currentWindow = updatedWall.woodWall.window;
    const doorWidth = configurator.getDoorWidth(currentDoor);
    const windowWidth = configurator.getWindowWidth(currentWindow);

    let needsUpdate = false;
    let removeDoor = false;
    let removeWindow = false;

    // Check if both door and window exist
    if (currentDoor !== "empty" && currentWindow !== "empty") {
      const totalRequired =
        doorWidth +
        windowWidth +
        padding +
        OPENING_BETWEEN_GAP;

      if (sectionWidth < totalRequired) {
        // Both don't fit together - first try removing just the window
        removeWindow = true;
        needsUpdate = true;

        // Check if door alone can fit after removing window
        if (sectionWidth < doorWidth + padding) {
          // Door alone doesn't fit either - remove both
          removeDoor = true;
        }
      }
    } else if (currentDoor !== "empty") {
      // Only door exists
      if (sectionWidth < doorWidth + padding) {
        removeDoor = true;
        needsUpdate = true;
      }
    } else if (currentWindow !== "empty") {
      // Only window exists
      if (sectionWidth < windowWidth + padding) {
        removeWindow = true;
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      updatedWall.woodWall = {
        ...updatedWall.woodWall,
        ...(removeDoor && { door: "empty" }),
        ...(removeWindow && { window: "empty" }),
      };
    }

    return updatedWall;
  });
}

/**
 * Get disabled values for windows based on current wall state
 */
export function getDisabledWindows(
  wall: Wall | undefined,
  configurator: ConfiguratorService,
  roofType: string,
  disableChoice?: boolean,
  availableWindows: string[] = ["raam", "vleugelraam"]
): string[] {
  if (!wall) return availableWindows;

  const disableValues: string[] = [];

  // Special case: kapschuur roof type on h-1 walls - disable all windows
  const isKapschuurH1 = roofType === "kapschuur" && wall.id.startsWith("h-1");
  const isKapschuurSide = roofType === "kapschuur" && wall.id.startsWith("v");
  const kapschuurSideOffset = isKapschuurSide ? 100 : 0;

  // Get section width
  const sectionWidth = configurator.getSectionWidth(wall);
  const padding = OPENING_SIDE_PADDING + kapschuurSideOffset

  // Get current door width
  const currentDoor = wall.woodWall.door || "empty";
  const doorWidth = configurator.getDoorWidth(currentDoor);

  if (disableChoice || isKapschuurH1 || sectionWidth === 0) {
    // Disable all windows
    return availableWindows;
  }

  // Check each window individually
  availableWindows.forEach((windowValue) => {
    const windowWidth = configurator.getWindowWidth(windowValue);

    // If there's a door, check if door + window can fit together
    if (currentDoor !== "empty") {
      if (
        sectionWidth <
        doorWidth +
        windowWidth +
        padding +
        OPENING_BETWEEN_GAP
      ) {
        disableValues.push(windowValue);
      }
    } else {
      // If no door, check if window alone can fit with padding
      if (sectionWidth < windowWidth + padding) {
        disableValues.push(windowValue);
      }
    }
  });

  return disableValues;
}

/**
 * Get disabled values for doors based on current wall state
 */
export function getDisabledDoors(
  wall: Wall | undefined,
  configurator: ConfiguratorService,
  roofType: string,
  availableDoors: string[] = [
    "dichte_73cm",
    "stapeldorpeldeur",
    "dubbele_dichte",
    "dubble_stapeldorpeldeur",
  ]
): string[] {
  if (!wall) return availableDoors;

  const disableValues: string[] = [];

  // Special case: kapschuur roof type on h-1 walls - disable all doors
  if (roofType === "kapschuur" && wall.id.startsWith("h-1")) {
    return availableDoors;
  }

  // Don't disable if wall has no wood wall
  if (wall.woodWall.outerColor === "empty") {
    return [];
  }

  // Get section width
  const sectionWidth = configurator.getSectionWidth(wall);
  if (sectionWidth === 0) return availableDoors; // Disable all if we can't determine width

  const isKapschuurSide = roofType === "kapschuur" && wall.id.startsWith("v");
  const kapschuurSideOffset = isKapschuurSide ? 100 : 0;

  // Check if door can fit considering current window
  const currentWindow = wall.woodWall.window;
  const padding = OPENING_SIDE_PADDING + kapschuurSideOffset;

  availableDoors.forEach((doorValue) => {
    const doorWidth = configurator.getDoorWidth(doorValue);

    // If there's a window, check if door + window can fit together
    if (currentWindow !== "empty") {
      const windowWidth = configurator.getWindowWidth(currentWindow);
      // Disable door if door + window + padding + spacing between them doesn't fit
      if (
        sectionWidth <
        doorWidth + windowWidth + padding + OPENING_BETWEEN_GAP
      ) {
        disableValues.push(doorValue);
      }
    } else {
      // If no window, check if door alone can fit with padding
      if (sectionWidth < doorWidth + padding) {
        disableValues.push(doorValue);
      }
    }
  });

  return disableValues;
}
